import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Image as ImageIcon,
  Sparkles,
  Eye,
  EyeOff,
  Trash2,
  Plus,
  Search,
  Wand2,
  CheckCircle,
  Camera,
  ChevronsUpDown,
  Check,
  Loader2,
  GripVertical,
  ArrowRight,
  Filter,
} from "lucide-react";
import {
  Button,
  Input,
  Label,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Badge,
} from "@/components/ui";
import { useToast } from "@/hooks";
import { httpRequest } from "@/lib";
import { PrivateGalleryImage } from "./private-gallery-image";
import {
  fetchGallery,
  uploadCase,
  analyzeCase,
  togglePublish,
  deleteCase,
  type BeforeAfterCase,
} from "@/services/gallery";

/* ── Skeleton ── */
function GallerySkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: i * 0.08, duration: 0.4 }}
          className="rounded-2xl bg-card border border-border/60 overflow-hidden"
        >
          <div className="h-60 bg-gradient-to-br from-muted to-muted/60 animate-pulse" />
          <div className="p-5 space-y-3">
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <div className="h-4 w-28 bg-muted rounded-md animate-pulse" />
                <div className="h-3 w-20 bg-muted/70 rounded-md animate-pulse" />
              </div>
              <div className="h-3 w-16 bg-muted/60 rounded-md animate-pulse" />
            </div>
            <div className="flex gap-2 pt-1">
              <div className="h-9 flex-1 bg-muted rounded-xl animate-pulse" />
              <div className="h-9 w-9 bg-muted rounded-xl animate-pulse" />
              <div className="h-9 w-9 bg-muted rounded-xl animate-pulse" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ── Case Card ── */
function CaseCard({
  c,
  onAnalyze,
  onTogglePublish,
  onDelete,
  index,
  onRefresh,
}: {
  c: BeforeAfterCase;
  onAnalyze: () => Promise<void>;
  onRefresh: () => void;
  onTogglePublish: () => void;
  onDelete: () => void;
  index: number;
}) {
  const [analyzing, setAnalyzing] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [photosUnavailable, setPhotosUnavailable] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    await onAnalyze();
    setAnalyzing(false);
  };

  const updatePosition = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setSliderPos(Math.max(3, Math.min(97, x)));
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e: MouseEvent) => { e.preventDefault(); updatePosition(e.clientX); };
    const handleUp = () => setIsDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => { window.removeEventListener("mousemove", handleMove); window.removeEventListener("mouseup", handleUp); };
  }, [isDragging, updatePosition]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches[0]) updatePosition(e.touches[0].clientX);
  }, [updatePosition]);

  const beforeSrc = c.before_image_full_url;
  const afterSrc = c.after_image_full_url;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.45, ease: [0.23, 1, 0.32, 1] }}
      className="group rounded-2xl bg-card border border-border/60 overflow-hidden transition-all duration-300 hover:border-border hover:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.06)]"
    >
      {/* Slider */}
      {photosUnavailable || !beforeSrc || !afterSrc ? (
        <div className="flex h-64 flex-col items-center justify-center gap-3 bg-app-surface-muted p-6 text-center">
          <ImageIcon className="size-6 text-app-fg-muted" aria-hidden="true" />
          <p className="text-sm text-app-fg-muted">Não foi possível carregar as fotos desta comparação.</p>
          <Button variant="outline" size="sm" onClick={onRefresh}>Recarregar fotos</Button>
        </div>
      ) : <div
        ref={containerRef}
        className="relative h-64 cursor-col-resize select-none overflow-hidden bg-zinc-100"
        onMouseDown={() => setIsDragging(true)}
        onTouchMove={handleTouchMove}
        onTouchStart={(e) => e.touches[0] && updatePosition(e.touches[0].clientX)}
      >
        <PrivateGalleryImage src={afterSrc} alt="Depois" onRetry={onRefresh} onUnavailable={() => setPhotosUnavailable(true)} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
          <PrivateGalleryImage
            src={beforeSrc} alt="Antes" onRetry={onRefresh} onUnavailable={() => setPhotosUnavailable(true)}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ minWidth: containerRef.current?.offsetWidth }}
          />
        </div>

        {/* Divider line + glow */}
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white z-10"
          style={{ left: `${sliderPos}%`, transform: "translateX(-50%)", boxShadow: "0 0 12px rgba(255,255,255,0.4)" }}
        />

        {/* Handle */}
        <div
          className="absolute top-1/2 z-20 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur-md shadow-[0_2px_16px_rgba(0,0,0,0.2)] flex items-center justify-center cursor-grab active:cursor-grabbing border border-white/20 transition-transform duration-200 hover:scale-110 active:scale-95"
          style={{ left: `${sliderPos}%` }}
        >
          <GripVertical className="w-4 h-4 text-zinc-400" />
        </div>

        {/* Labels - glassmorphism */}
        <div className="absolute top-3 left-3 bg-zinc-950/50 backdrop-blur-xl text-white text-[10px] font-semibold tracking-widest uppercase px-3 py-1.5 rounded-lg border border-white/[0.08]">
          Antes
        </div>
        <div className="absolute top-3 right-3 bg-zinc-950/50 backdrop-blur-xl text-white text-[10px] font-semibold tracking-widest uppercase px-3 py-1.5 rounded-lg border border-white/[0.08]">
          Depois
        </div>

        {/* Published badge */}
        {c.is_published && (
          <div className="absolute bottom-3 right-3 bg-emerald-500/80 backdrop-blur-xl text-white text-[10px] font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 border border-emerald-400/20">
            <Eye className="w-3 h-3" /> Publicado
          </div>
        )}
      </div>}

      {/* Content */}
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-foreground text-[15px] tracking-tight truncate">{c.procedure}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{c.patient?.person?.name || c.patient?.name || "Paciente"}</p>
          </div>
          <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap pt-0.5">
            {new Date(c.created_at).toLocaleDateString("pt-BR")}
          </span>
        </div>

        {/* Analysis */}
        {c.analysis_status === "completed" && c.analysis?.metrics && (
          <div className="flex items-center gap-0 rounded-xl overflow-hidden border border-border/60">
            <div className="flex-1 text-center py-3 bg-rose-50/50">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Simetria</p>
              <p className="text-lg font-bold text-rose-600 tabular-nums mt-0.5">
                {c.analysis.metrics.symmetry_score_before}%
              </p>
            </div>
            <div className="w-8 flex items-center justify-center bg-muted/30">
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50" />
            </div>
            <div className="flex-1 text-center py-3 bg-emerald-50/50">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Simetria</p>
              <p className="text-lg font-bold text-emerald-600 tabular-nums mt-0.5">
                {c.analysis.metrics.symmetry_score_after}%
              </p>
            </div>
          </div>
        )}

        {c.analysis_status === "completed" && c.analysis?.analysis_summary && (
          <p className="text-[13px] text-muted-foreground leading-relaxed line-clamp-2">
            {c.analysis.analysis_summary}
          </p>
        )}

        {!c.analysis_available && c.analysis_status !== "completed" && (
          <p className="text-xs text-app-fg-muted">{c.analysis_unavailable_reason || "A análise por IA está temporariamente indisponível. Você pode continuar comparando as fotos."}</p>
        )}
        {/* Actions */}
        <div className="flex items-center gap-2 pt-0.5">
          {c.analysis_status !== "completed" ? (
            <Button
              size="sm"
              variant="outline"
              className="text-xs gap-2 flex-1 h-9 rounded-xl border-border/60 hover:bg-muted/50 transition-all duration-200 active:scale-[0.97]"
              onClick={handleAnalyze}
              disabled={analyzing || !c.analysis_available}
            >
              {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              {analyzing ? "Analisando..." : c.analysis_available ? "Analisar IA" : "Análise por IA indisponível"}
            </Button>
          ) : (
            <span className="text-[12px] text-emerald-600 flex items-center gap-1.5 flex-1 font-medium">
              <CheckCircle className="w-4 h-4" /> Análise concluída
            </span>
          )}
          <Button
            size="sm" variant="ghost"
            className="h-9 w-9 p-0 rounded-xl hover:bg-muted/60 transition-all duration-200 active:scale-[0.93]"
            onClick={onTogglePublish}
            title={c.is_published ? "Despublicar" : "Publicar"}
          >
            {c.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            size="sm" variant="ghost"
            className="h-9 w-9 p-0 rounded-xl text-destructive hover:text-destructive hover:bg-destructive/5 transition-all duration-200 active:scale-[0.93]"
            onClick={onDelete}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Patient Combobox ── */
type PatientOption = { id: string; name: string };

function PatientCombobox({ value, onChange }: { value: string; onChange: (id: string, name: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const doSearch = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setOptions([]); return; }
    setSearching(true);
    try {
      const { data } = await httpRequest.get(`/patients?search=${encodeURIComponent(q)}&per_page=10`);
      const raw = data?.items ?? data?.data ?? data ?? [];
      setOptions(raw.map((p: any) => ({
        id: p.id,
        name: p.person?.name || p.name || "Sem nome",
      })));
    } catch { setOptions([]); }
    setSearching(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, doSearch]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal mt-1.5 h-10 rounded-xl border-border/60">
          {value ? (selectedName || "Paciente selecionado") : "Buscar paciente..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-40" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Digite o nome do paciente..." value={search} onValueChange={setSearch} />
          <CommandList>
            {searching ? (
              <div className="flex items-center justify-center py-6 gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Buscando...</span>
              </div>
            ) : options.length === 0 ? (
              <CommandEmpty>{search.length < 2 ? "Digite pelo menos 2 caracteres..." : "Nenhum paciente encontrado."}</CommandEmpty>
            ) : (
              <CommandGroup>
                {options.map((p) => (
                  <CommandItem key={p.id} value={p.id} onSelect={() => { onChange(p.id, p.name); setSelectedName(p.name); setOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100" : "opacity-0"}`} />
                    {p.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ── Procedure Combobox ── */
type ProcedureOption = { id: string; name: string };

function ProcedureCombobox({ value, onChange }: { value: string; onChange: (name: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<ProcedureOption[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const doSearch = useCallback(async (q: string) => {
    if (!q || q.length < 2) { setOptions([]); return; }
    setSearching(true);
    try {
      const { data } = await httpRequest.get(`/procedures?search=${encodeURIComponent(q)}&per_page=10`);
      const raw = data?.items ?? data?.data ?? data ?? [];
      setOptions(raw.map((p: any) => ({
        id: p.id || p.name,
        name: p.name || "Sem nome",
      })));
    } catch { setOptions([]); }
    setSearching(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, doSearch]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal mt-1.5 h-10 rounded-xl border-border/60">
          {value || "Buscar procedimento..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-40" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Digite o nome do procedimento..." value={search} onValueChange={setSearch} />
          <CommandList>
            {searching ? (
              <div className="flex items-center justify-center py-6 gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Buscando...</span>
              </div>
            ) : options.length === 0 ? (
              <CommandEmpty>
                {search.length < 2 ? "Digite pelo menos 2 caracteres..." : (
                  <div className="space-y-2">
                    <p>Nenhum procedimento encontrado.</p>
                    {search.length >= 2 && (
                      <button
                        className="text-xs text-brand hover:underline"
                        onClick={() => { onChange(search); setOpen(false); }}
                      >
                        Usar &quot;{search}&quot; como procedimento
                      </button>
                    )}
                  </div>
                )}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {options.map((p) => (
                  <CommandItem key={p.id} value={p.id} onSelect={() => { onChange(p.name); setOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${value === p.name ? "opacity-100" : "opacity-0"}`} />
                    {p.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ── Main ── */
export function BeforeAfterGallery() {
  const { toast } = useToast();
  const [cases, setCases] = useState<BeforeAfterCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filterProcedure, setFilterProcedure] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const [patientId, setPatientId] = useState("");
  const [procedure, setProcedure] = useState("");
  const [context, setContext] = useState("");
  const [beforeFile, setBeforeFile] = useState<File | null>(null);
  const [afterFile, setAfterFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const beforeRef = useRef<HTMLInputElement>(null);
  const afterRef = useRef<HTMLInputElement>(null);

  async function load() {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await fetchGallery(filterProcedure ? { procedure: filterProcedure } : undefined);
      setCases(Array.isArray(data) ? data : []);
    } catch { setLoadError(true); }
    setLoading(false);
  }

  useEffect(() => { load(); }, [filterProcedure]);

  async function handleUpload() {
    if (!patientId || !procedure || !beforeFile || !afterFile) {
      toast({ title: "Preencha todos os campos e selecione as duas imagens.", variant: "destructive" });
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("patient_id", patientId);
    fd.append("procedure", procedure);
    fd.append("before_image", beforeFile);
    fd.append("after_image", afterFile);
    if (context) fd.append("context", context);
    try {
      await uploadCase(fd);
      toast({ title: "Caso adicionado com sucesso!" });
      setDialogOpen(false);
      setPatientId(""); setProcedure(""); setContext(""); setBeforeFile(null); setAfterFile(null);
      load();
    } catch (err: any) {
      const detail = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Erro desconhecido";

      toast({ title: "Erro ao fazer upload", description: detail, variant: "destructive" });
    }
    setUploading(false);
  }

  async function handleAnalyze(id: string) {
    try { await analyzeCase(id); toast({ title: "Caso analisado com sucesso!" }); load(); }
    catch { toast({ title: "Erro na análise por IA", variant: "destructive" }); }
  }

  async function handleToggle(id: string) { await togglePublish(id); load(); }
  async function handleDelete(id: string) { await deleteCase(id); toast({ title: "Caso removido" }); load(); }

  const total = cases.length;
  const analyzed = cases.filter(c => c.analysis_status === "completed").length;
  const published = cases.filter(c => c.is_published).length;
  const procedures = [...new Set(cases.map(c => c.procedure).filter(Boolean))];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Galeria Antes/Depois</h1>
          <p className="text-sm text-muted-foreground mt-1">Fotos de antes e depois para acompanhar a evolução dos pacientes.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 h-10 px-5 bg-brand hover:bg-brand-hover text-brand-fg rounded-xl transition-all duration-200 active:scale-[0.97] shadow-sm">
              <Plus className="w-4 h-4" /> Novo Caso
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg">Adicionar Caso</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              <div>
                <Label className="text-sm font-medium">Paciente</Label>
                <PatientCombobox value={patientId} onChange={(id) => setPatientId(id)} />
              </div>
              <div>
                <Label className="text-sm font-medium">Procedimento</Label>
                <ProcedureCombobox value={procedure} onChange={(name) => setProcedure(name)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Foto Antes</Label>
                  <div
                    className="mt-1.5 border-2 border-dashed border-border/60 rounded-xl h-36 flex items-center justify-center cursor-pointer hover:border-zinc-400 hover:bg-muted/30 transition-all duration-200 overflow-hidden"
                    onClick={() => beforeRef.current?.click()}
                  >
                    {beforeFile ? (
                      <img src={URL.createObjectURL(beforeFile)} className="w-full h-full object-cover" alt="Antes" />
                    ) : (
                      <div className="text-center space-y-1.5">
                        <ImageIcon className="w-5 h-5 text-muted-foreground/60 mx-auto" />
                        <p className="text-xs text-muted-foreground">Clique para selecionar</p>
                      </div>
                    )}
                  </div>
                  <input ref={beforeRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && setBeforeFile(e.target.files[0])} />
                </div>
                <div>
                  <Label className="text-sm font-medium">Foto Depois</Label>
                  <div
                    className="mt-1.5 border-2 border-dashed border-border/60 rounded-xl h-36 flex items-center justify-center cursor-pointer hover:border-zinc-400 hover:bg-muted/30 transition-all duration-200 overflow-hidden"
                    onClick={() => afterRef.current?.click()}
                  >
                    {afterFile ? (
                      <img src={URL.createObjectURL(afterFile)} className="w-full h-full object-cover" alt="Depois" />
                    ) : (
                      <div className="text-center space-y-1.5">
                        <ImageIcon className="w-5 h-5 text-muted-foreground/60 mx-auto" />
                        <p className="text-xs text-muted-foreground">Clique para selecionar</p>
                      </div>
                    )}
                  </div>
                  <input ref={afterRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && setAfterFile(e.target.files[0])} />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Contexto Clínico <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                <Textarea value={context} onChange={e => setContext(e.target.value)} placeholder="Descreva o procedimento realizado..." className="mt-1.5 rounded-xl border-border/60 min-h-[80px]" />
              </div>
              <Button
                onClick={handleUpload} disabled={uploading}
                className="w-full gap-2 h-11 bg-brand hover:bg-brand-hover text-brand-fg rounded-xl transition-all duration-200 active:scale-[0.98]"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {uploading ? "Enviando..." : "Adicionar Caso"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Metrics strip */}
      <div className="flex items-center gap-8 py-4 px-6 rounded-2xl bg-muted/30 border border-border/40">
        <div>
          <p className="text-3xl font-bold text-foreground tracking-tight tabular-nums">{total}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Total de Casos</p>
        </div>
        <div className="w-px h-10 bg-border/60" />
        <div>
          <p className="text-3xl font-bold text-foreground tracking-tight tabular-nums">{analyzed}</p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-500" /> Analisados por IA
          </p>
        </div>
        <div className="w-px h-10 bg-border/60" />
        <div>
          <p className="text-3xl font-bold text-foreground tracking-tight tabular-nums">{published}</p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            <Eye className="w-3 h-3 text-emerald-500" /> Publicados
          </p>
        </div>
        {analyzed > 0 && total > 0 && (
          <>
            <div className="w-px h-10 bg-border/60" />
            <div>
              <p className="text-3xl font-bold text-foreground tracking-tight tabular-nums">
                {Math.round((analyzed / total) * 100)}%
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Taxa de análise</p>
            </div>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
          <Input
            placeholder="Filtrar por procedimento..."
            className="pl-10 h-10 rounded-xl border-border/60 bg-muted/20 focus:bg-card transition-colors"
            value={filterProcedure}
            onChange={e => setFilterProcedure(e.target.value)}
          />
        </div>
        {procedures.length > 0 && (
          <Select value={filterProcedure || "all"} onValueChange={(v) => setFilterProcedure(v === "all" ? "" : v ?? "")}>
            <SelectTrigger className="w-52 h-10 rounded-xl border-border/60">
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-muted-foreground" />
                <SelectValue placeholder="Procedimento" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {procedures.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {filterProcedure && (
          <Badge variant="secondary" className="gap-1 rounded-lg cursor-pointer hover:bg-muted" onClick={() => setFilterProcedure("")}>
            {filterProcedure} <span className="text-muted-foreground">&times;</span>
          </Badge>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <GallerySkeleton />
      ) : loadError ? (
        <div role="alert" className="rounded-xl border border-app-border p-6">
          <p>Não foi possível carregar a galeria.</p>
          <Button variant="outline" className="mt-3" onClick={() => void load()}>Tentar novamente</Button>
        </div>
      ) : cases.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl border-2 border-dashed border-border/50 py-24 text-center"
        >
          <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto mb-5">
            <Camera className="w-7 h-7 text-muted-foreground/60" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Nenhum caso registrado</h3>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto leading-relaxed">
            Adicione fotos antes e depois para acompanhar a evolução estética dos seus pacientes.
          </p>
          <Button
            className="mt-6 gap-2 h-10 px-6 bg-brand hover:bg-brand-hover text-brand-fg rounded-xl transition-all duration-200 active:scale-[0.97]"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-4 h-4" /> Adicionar primeiro caso
          </Button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {cases.map((c, i) => (
              <CaseCard
                key={c.id}
                c={c}
                index={i}
                onRefresh={() => void load()}
                onAnalyze={() => handleAnalyze(c.id)}
                onTogglePublish={() => handleToggle(c.id)}
                onDelete={() => handleDelete(c.id)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
